import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Building2, Award, FileText, ExternalLink, Scale, Shield, Copyright, AlertCircle } from 'lucide-react';
import SEOHead from './seo/SEOHead';
import { BreadcrumbSchema } from './seo/SchemaMarkup';
import { PAGE_SEO, SITE_URL } from '../seoConfig';

const Impressum = () => {
  const breadcrumbs = [
    { name: "Start", url: SITE_URL },
    { name: "Impressum", url: `${SITE_URL}/impressum` }
  ];

  return (
    <>
      <SEOHead 
        title={PAGE_SEO.impressum.title}
        description={PAGE_SEO.impressum.description}
        canonical={PAGE_SEO.impressum.canonical}
      />
      <BreadcrumbSchema items={breadcrumbs} />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-[#DC2626] transition-colors duration-300"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Zurück zur Startseite</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="impressum-title">
              Impressum
            </h1>
            <div className="w-24 h-1 bg-[#DC2626] mx-auto rounded-full"></div>
          </div>

          {/* Content Cards */}
          <div className="space-y-8">
            
            {/* Angaben gemäß § 5 TMG */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="company-info-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Angaben gemäß § 5 TMG und § 18 MStV</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-xl font-semibold text-gray-900">Haxhi Kyreziu</p>
                <p className="text-lg font-medium text-[#DC2626]">HK Altbausanierung</p>
                
                <div className="flex items-start space-x-3 pt-4">
                  <MapPin className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-1" />
                  <div>
                    <p>Gartenstraße 16</p>
                    <p>73061 Ebersbach an der Fils</p>
                    <p>Deutschland</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#DC2626] flex-shrink-0" />
                  <a href="tel:01729308274" className="hover:text-[#DC2626] transition-colors">0172 9308274</a>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#DC2626] flex-shrink-0" />
                  <a href="mailto:hk-altbausanierung@outlook.de" className="hover:text-[#DC2626] transition-colors">hk-altbausanierung@outlook.de</a>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <p><span className="font-semibold">Rechtsform:</span> Einzelunternehmen</p>
                </div>
              </div>
            </div>

            {/* Handwerkskammer */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="chamber-info-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Award className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Zuständige Handwerkskammer</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="font-semibold text-lg">Handwerkskammer Region Stuttgart</p>
                <p>Eingetragen in der Handwerksrolle</p>
                <p><span className="font-semibold">Betriebsnummer (Handwerkskammer):</span> 670102508</p>
                
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold mb-2">Berufsbezeichnungen:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Holz- und Bautenschützer</li>
                    <li className="ml-4 text-gray-600">(Mauerschutz und Holzimprägnierung in Gebäuden)</li>
                    <li>Dachdecker</li>
                  </ul>
                  <p className="mt-4 text-gray-600 italic">Die Berufsbezeichnungen wurden verliehen in der Bundesrepublik Deutschland.</p>
                </div>
              </div>
            </div>

            {/* Haftung für Inhalte */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="content-liability-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Haftung für Inhalte</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.</p>
                <p>Nach §§ 8 bis 10 TMG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.</p>
                <p>Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.</p>
              </div>
            </div>

            {/* Haftung für Links */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="links-liability-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <ExternalLink className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Haftung für Links</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Unsere Website enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben.</p>
                <p>Deshalb können wir für diese fremden Inhalte keine Gewähr übernehmen.</p>
                <p>Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.</p>
              </div>
            </div>

            {/* Urheberrecht */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="copyright-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Copyright className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Urheberrecht</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht.</p>
                <p>Beiträge Dritter sind als solche gekennzeichnet.</p>
              </div>
            </div>

            {/* Online-Streitbeilegung */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="dispute-resolution-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Online-Streitbeilegung</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:</p>
                <a 
                  href="https://ec.europa.eu/consumers/odr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-[#DC2626] hover:underline font-medium"
                >
                  <span>https://ec.europa.eu/consumers/odr/</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <p className="pt-2">Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
              </div>
            </div>

            {/* Inhaltlich verantwortlich */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="responsible-person-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV</h2>
              </div>
              
              <div className="space-y-2 text-gray-700 leading-relaxed">
                <p className="font-semibold text-lg">Haxhi Kyreziu</p>
                <p>Gartenstraße 16</p>
                <p>73061 Ebersbach an der Fils</p>
              </div>
            </div>

          </div>

          {/* Back Button */}
          <div className="text-center mt-12">
            <Link 
              to="/" 
              className="inline-flex items-center space-x-2 px-8 py-4 bg-[#DC2626] text-white rounded-full font-semibold hover:bg-[#B91C1C] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              data-testid="back-to-home-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Zurück zur Startseite</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Impressum;
